import React, { useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import { createLocation, updateLocation } from '../../actions/locationActions';
import useAuthUser from '../../hooks/useAuthUser';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: '#1976D2',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const defaultState = { name: '', shippingAddress: '', shippingCity: '', shippingState: '', shippingZip: '', phone: '', active: true, accountId: '', userId: [] }

const inputStyle = {
  display: "block",
  padding: "1.1rem 0.75rem",
  width: "100%",
  fontSize: "0.9rem",
  lineHeight: 1.25,
  color: "#55595c",
  backgroundColor: "#fff",
  backgroundImage: "none",
  backgroundClip: "padding-box",
  borderTop: "0",
  borderRight: "0",
  borderBottom: "1px solid #eee",
  borderLeft: "0",
  borderRadius: "3px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 1, 1)"
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC'
]

const AddLocation = ({ setOpen, open, currentId, setCurrentId, defaultAccountId, onSaved }) => {
  const authData = useAuthUser()
  const dispatch = useDispatch()
  const location = useSelector((state) => currentId ? state.locations.locations.find((l) => l._id === currentId) : null)
  const accounts = useSelector((state) => state.accounts.accounts || [])
  const [locationData, setLocationData] = useState(defaultState)
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    if (location) {
      setLocationData(location)
    }
  }, [location])

  useEffect(() => {
    const userId = authData?.result?._id || authData?.result?.googleId
    if (userId) {
      setLocationData((prev) => ({ ...prev, userId: [userId] }))
    }
  }, [authData])

  const clear = () => {
    setCurrentId(null)
    setLocationData(defaultState)
  }

  useEffect(() => {
    if (defaultAccountId && !locationData.accountId) {
      setLocationData((prev) => ({ ...prev, accountId: defaultAccountId }))
    }
  }, [defaultAccountId, locationData.accountId])

  const handleClose = () => {
    setOpen(false);
    clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      ...locationData,
      accountId: locationData.accountId || defaultAccountId || locationData.accountId,
    }
    let saved = null
    if (currentId) {
      saved = await dispatch(updateLocation(currentId, payload, openSnackbar))
    } else {
      saved = await dispatch(createLocation(payload, openSnackbar))
    }
    if (saved && typeof onSaved === 'function') onSaved(saved)
    handleClose()
  }

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} fullWidth>
      <DialogTitle id="customized-dialog-title" onClose={handleClose} style={{ paddingLeft: '20px', color: 'white' }}>
        {currentId ? 'Edit Location' : 'Add Location'}
      </DialogTitle>
      <DialogContent dividers>
        <div className="customInputs">
          <input
            placeholder="Name"
            style={inputStyle}
            name='name'
            type='text'
            onChange={(e) => setLocationData({ ...locationData, name: e.target.value })}
            value={locationData.name}
          />
          <select
            style={inputStyle}
            name='accountId'
            onChange={(e) => setLocationData({ ...locationData, accountId: e.target.value })}
            value={locationData.accountId || ''}
          >
            <option value="" disabled>Parent Account</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>{a.name}</option>
            ))}
          </select>
          <input
            placeholder="Shipping Address"
            style={inputStyle}
            name='shippingAddress'
            type='text'
            onChange={(e) => setLocationData({ ...locationData, shippingAddress: e.target.value })}
            value={locationData.shippingAddress}
          />
          <input
            placeholder="City"
            style={inputStyle}
            name='shippingCity'
            type='text'
            onChange={(e) => setLocationData({ ...locationData, shippingCity: e.target.value })}
            value={locationData.shippingCity}
          />
          <select
            style={inputStyle}
            name='shippingState'
            onChange={(e) => setLocationData({ ...locationData, shippingState: e.target.value })}
            value={locationData.shippingState}
          >
            <option value="" disabled>State</option>
            {US_STATES.map((abbr) => (
              <option key={abbr} value={abbr}>{abbr}</option>
            ))}
          </select>
          <input
            placeholder="Zip"
            style={inputStyle}
            name='shippingZip'
            type='text'
            onChange={(e) => setLocationData({ ...locationData, shippingZip: e.target.value })}
            value={locationData.shippingZip}
          />
          <input
            placeholder="Phone"
            style={inputStyle}
            name='phone'
            type='text'
            onChange={(e) => setLocationData({ ...locationData, phone: e.target.value })}
            value={locationData.phone}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="checkbox"
              checked={Boolean(locationData.active)}
              onChange={(e) => setLocationData({ ...locationData, active: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            Active
          </label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained" color="primary" style={{ marginRight: '16px' }}>
          Save Location
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddLocation
